import React, { useState } from "react";
import * as XLSX from "xlsx";
import UploadConfirmation from "./UploadConfirmation";

const UploadExcelFile = ({
  onClose,
  uploadType, // Pass the selected campaign type
  handleCloseModal,
  fetchContacts,
}) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [step, setStep] = useState(1); // Tracks which step to display

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file); // Save uploaded file name
      setStep(2); // Go to UploadConfirmation
    }
  };

  const downloadSampleExcel = () => {
    // Sample data for the Excel file
    const sampleData = [
      {
        Email: "john.doe@example.com",
        Phone: "1234567890",
        Name: "John Doe",
        Company: "ABC Corp",
        Notes: "VIP Customer",
      },
      {
        Email: "jane.smith@gmail.com",
        Phone: "1987654321",
        Name: "Jane Smith",
        Company: "XYZ Ltd",
        Notes: "Newsletter subscriber",
      },
      {
        Email: "mike.johnson@company.com",
        Phone: "1122334455",
        Name: "Mike Johnson",
        Company: "Tech Solutions",
        Notes: "Potential lead",
      },
      {
        Email: "sarah.wilson@email.com",
        Phone: "1555666777",
        Name: "Sarah Wilson",
        Company: "Marketing Inc",
        Notes: "Event attendee",
      },
      {
        Email: "david.brown@business.com",
        Phone: "1888999000",
        Name: "David Brown",
        Company: "Sales Co",
        Notes: "Referral contact",
      },
    ];

    // Create a new workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(sampleData);

    // Set column widths for better readability
    const columnWidths = [
      { wch: 25 }, // Email
      { wch: 15 }, // Phone Number
      { wch: 15 }, // Name
      { wch: 15 }, // Company
      { wch: 20 }, // Notes
    ];
    worksheet["!cols"] = columnWidths;

    // Add the worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Contacts");

    // Generate Excel file and trigger download
    XLSX.writeFile(workbook, "contacts_sample.xlsx");
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (step === 2 && uploadedFile) {
    return (
      <UploadConfirmation
        fetchContacts={fetchContacts}
        handleCloseModal={handleCloseModal}
        excelFile={uploadedFile} // Passing the raw Excel file directly
        fileName={uploadedFile.name}
        onClose={onClose}
        channel={uploadType === "excel" ? "excel" : "copy-paste"}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white w-full max-w-sm sm:max-w-md lg:max-w-lg xl:max-w-2xl rounded-lg shadow-lg relative max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-base sm:text-lg font-bold text-gray-800">
            Upload your Excel File
          </h2>
          <button
            className="text-gray-500 hover:text-gray-800 text-xl p-1 hover:bg-gray-100 rounded-full transition-colors"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {/* Download Sample Button */}
          <div className="mb-4 sm:mb-6 text-center">
            <button
              onClick={downloadSampleExcel}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Download Sample Excel
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Download a sample file to see the correct format
            </p>
          </div>
          {/* Display Uploaded File */}
          {uploadedFile && (
            <div className="mb-6">
              <p className="text-gray-700 text-sm sm:text-base font-medium mb-2">
                Uploaded File:
              </p>
              <span className="text-gray-600 text-sm break-all bg-gray-50 px-3 py-2 rounded-md border">
                {uploadedFile.name}
              </span>
            </div>
          )}

          {/* Upload Box */}
          <div className="flex justify-center items-center w-full h-40 sm:h-48 lg:h-56 bg-blue-50 rounded-lg shadow-inner border-dashed border-2 border-blue-300 hover:bg-blue-100 transition-colors">
            <label
              htmlFor="file-upload"
              className="flex flex-col items-center cursor-pointer p-4 text-center"
            >
              <div className="text-4xl sm:text-5xl text-blue-400 mb-2 sm:mb-3">
                ⬆️
              </div>
              <p className="text-gray-600 text-sm sm:text-base font-medium">
                Upload
              </p>
              <p className="text-gray-500 text-xs sm:text-sm mt-1">
                Click to select CSV or Excel file
              </p>
              <input
                id="file-upload"
                type="file"
                className="hidden"
                accept=".csv, .xlsx"
                onChange={handleFileUpload}
              />
            </label>
          </div>

          {/* Additional Info */}
          <div className="mt-4 sm:mt-6 text-center">
            <p className="text-xs sm:text-sm text-gray-500">
              Supported formats: CSV, XLSX
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadExcelFile;
